import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BlobField from '../components/BlobField';
import LogoMark from '../components/LogoMark';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../api';

/**
 * 19 — Sign In (matches Claude's 19-sign-in.html exactly, ported to React).
 */
export default function SignIn() {
  const navigate = useNavigate();
  const { login, refreshMe } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true);
    setErr('');
    try {
      const user = await login({ email, password, remember });
      if (user && !user.profile_complete) {
        navigate('/profile-setup', { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || t('auth.signInFallbackErr'));
      setBusy(false);
    }
  };

  return (
    <>
      <BlobField />
      <div className="stage">
        <form className="card" onSubmit={submit} data-testid="signin-form">
          <LogoMark size={56} />
          <h1 className="heading">{t('auth.signInTitle')}</h1>
          <p className="subcopy">{t('auth.signInSubcopy')}</p>

          <div className="field">
            <label className="field-label"><span>{t('common.email')}</span></label>
            <input
              type="email"
              className="field-input"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="signin-email-input"
            />
          </div>
          <div className="field">
            <label className="field-label"><span>{t('common.password')}</span></label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              testid="signin-password-input"
              toggleTestid="signin-password-toggle"
            />
          </div>
          <div className="forgot-row">
            <label className="remember-me" data-testid="signin-remember-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="signin-remember-checkbox"
              />
              <span>{t('auth.rememberMe')}</span>
            </label>
            <Link to="/forgot-password" data-testid="forgot-password-link">{t('auth.forgotPassword')}</Link>
          </div>

          {err && (
            <p style={{ color: 'var(--error)', fontSize: 13, margin: '0 0 12px', fontWeight: 700 }} data-testid="signin-error">
              {err}
            </p>
          )}

          <button type="submit" className="btn-primary" disabled={!email || !password || busy} data-testid="signin-submit-btn">
            {busy ? t('auth.signInBusy') : t('auth.signInCta')}
          </button>

          <div className="divider-row"><div className="line" /><span>{t('onboarding.orDivider')}</span><div className="line" /></div>

          <button
            type="button"
            className="btn-google"
            disabled
            data-testid="signin-google-btn"
            title="Coming at main launch"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
              <path d="M19.6 10.23c0-.68-.06-1.34-.17-1.97H10v3.73h5.4a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.9-1.75 2.97-4.33 2.97-7.28z" fill="#4285F4" />
              <path d="M10 20c2.7 0 4.97-.9 6.63-2.44l-3.24-2.5c-.9.6-2.04.96-3.39.96-2.61 0-4.82-1.76-5.6-4.13H1.05v2.6A10 10 0 0 0 10 20z" fill="#34A853" />
              <path d="M4.4 11.89A6.01 6.01 0 0 1 4.08 10c0-.66.11-1.3.32-1.9V5.51H1.05a10 10 0 0 0 0 8.98l3.35-2.6z" fill="#FBBC04" />
              <path d="M10 3.96c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.98 9.98 0 0 0 10 0 10 10 0 0 0 1.05 5.51l3.35 2.6C5.18 5.72 7.39 3.96 10 3.96z" fill="#EA4335" />
            </svg>
            {t('auth.continueGoogle')}
          </button>
          <p className="btn-google-note">{t('auth.googleNote')}</p>

          <p className="signup-note" style={{ marginTop: 14 }}>
            {t('auth.signInNewHere')} <Link to="/onboarding" data-testid="signin-onboarding-link">{t('auth.enterInviteCode')}</Link>
          </p>
        </form>
      </div>
    </>
  );
}
